import { createClient } from '@supabase/supabase-js'
import { createApiOperation } from '../utils/apiErrorHandler'
import { getEnvVar } from '../utils/envValidation'

// Get Supabase configuration from environment variables
// These should be set in your .env file
let supabaseUrl: string | undefined
let supabaseAnonKey: string | undefined

// Directly access process.env for Node.js environment
if (typeof process !== 'undefined' && process.env) {
  supabaseUrl = process.env.VITE_SUPABASE_URL
  supabaseAnonKey = process.env.VITE_SUPABASE_ANON_KEY
} else {
  // Fallback to getEnvVar for browser environment
  supabaseUrl = getEnvVar('VITE_SUPABASE_URL')
  supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY')
}

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase configuration:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey,
    url: supabaseUrl,
    key: supabaseAnonKey,
    processEnvUrl: typeof process !== 'undefined' ? process.env.VITE_SUPABASE_URL : 'N/A',
    processEnvKey: typeof process !== 'undefined' ? process.env.VITE_SUPABASE_ANON_KEY : 'N/A'
  })
  throw new Error('Missing Supabase configuration. Please check your .env file.')
}

// Validate URL format
if (!supabaseUrl.startsWith('https://') || !supabaseUrl.includes('.supabase.co')) {
  console.error('❌ Invalid Supabase URL format:', supabaseUrl)
  throw new Error('Invalid Supabase URL format. Should be https://your-project.supabase.co')
}

// Debug logging for configuration check (only in development)
let isDev = false
try {
  isDev = typeof process !== 'undefined' && process.env.NODE_ENV === 'development'
} catch {
  // Check for browser environment
  try {
    if (typeof window !== 'undefined') {
      isDev = (import.meta as any).env?.DEV
    }
  } catch {
    // Default to false
  }
}

if (isDev) {
  console.log('✅ Supabase config validated:', {
    url: `${supabaseUrl.substring(0, 30)}...`,
    keyLength: supabaseAnonKey.length
  })
}

/**
 * Supabase client instance
 * 
 * Configured with:
 * - PKCE flow for enhanced security
 * - Persistent sessions in localStorage (browser only)
 * - Auto token refresh
 * - Session detection from URL (for magic links, etc.)
 */
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    persistSession: typeof window !== 'undefined',        // Keep user logged in across browser sessions (browser only)
    autoRefreshToken: typeof window !== 'undefined',      // Automatically refresh expired tokens (browser only)
    detectSessionInUrl: typeof window !== 'undefined',    // Handle auth callbacks from email links (browser only)
    flowType: 'pkce',           // Use PKCE flow for enhanced security
    storage: typeof window !== 'undefined' ? window.localStorage : undefined, // Use localStorage for persistence (browser only)
  }
})

/**
 * Clear all authentication data and reload the page
 * 
 * This is a nuclear option for when auth state gets corrupted.
 * It clears all storage and forces a page reload to reset everything.
 */
export const clearAuthState = async () => {
  try {
    // Sign out from Supabase
    await supabase.auth.signOut()
    
    // Clear all browser storage
    localStorage.clear()
    sessionStorage.clear()
    
    // Force reload to clear any cached state
    window.location.reload()
  } catch (error) {
    console.error('Error clearing auth state:', error)
    // Force reload anyway
    window.location.reload()
  }
}

/**
 * Test database connection
 * 
 * Attempts a simple query to verify that:
 * 1. Supabase configuration is correct
 * 2. Database is accessible
 * 3. Basic permissions are working
 * 
 * @returns {Promise<{success: boolean, error?: Error}>}
 */
export const testConnection = async () => {
  try {
    console.log('🔍 Testing Supabase connection...')

    // Test with a timeout
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Connection timeout after 10 seconds')), 10000)
    )

    const testPromise = supabase
      .from('organizations')
      .select('count')
      .limit(1)

    const raceResult = await Promise.race([testPromise, timeoutPromise])
    const { error } = raceResult

    if (error) {
      console.error('❌ Database connection failed:', error)
      return { success: false, error }
    }

    console.log('✅ Database connection successful')
    return { success: true }
  } catch (error) {
    console.error('❌ Connection test failed:', error)
    return { success: false, error }
  }
}

/**
 * Enhanced Supabase operations with automatic error handling and retry logic
 */
export const supabaseApi = {
  // Select operations with error handling
  select: createApiOperation(
    async (table: string, options: any = {}) => {
      try {
        const { data, error } = await supabase
          .from(table)
          .select(options.select || '*')
          .match(options.match || {})
          .eq(options.eq ? Object.keys(options.eq)[0] : '', options.eq ? Object.values(options.eq)[0] : '')
          .limit(options.limit || 1000)

        if (error) {
          // Handle schema evolution issues gracefully
          if (error.code === '42703' || error.message?.includes('column') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Column missing in table '${table}'. This may be expected during development.`, error)
            return [] // Return empty array for missing columns
          }
          if (error.code === '42P01' || error.message?.includes('relation') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Table '${table}' does not exist. This may be expected during development.`, error)
            return [] // Return empty array for missing tables
          }
          throw error
        }
        return data
      } catch (error: any) {
        // Handle network and other errors
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          throw new Error('Database connection failed. Please check your internet connection.')
        }
        throw error
      }
    },
    {
      retry: { maxRetries: 2 },
      errorMessage: 'Failed to load data'
    }
  ),

  // Insert operations with error handling
  insert: createApiOperation(
    async (table: string, data: any, options: any = {}) => {
      try {
        const { data: result, error } = await supabase
          .from(table)
          .insert(data)
          .select(options.select || '*')

        if (error) {
          // Handle schema evolution issues gracefully
          if (error.code === '42703' || error.message?.includes('column') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Column missing in table '${table}'. Cannot insert data.`, error)
            throw new Error(`Database schema issue: Missing columns in table '${table}'. Please run database migrations.`)
          }
          if (error.code === '42P01' || error.message?.includes('relation') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Table '${table}' does not exist. Cannot insert data.`, error)
            throw new Error(`Database schema issue: Table '${table}' does not exist. Please run database migrations.`)
          }
          throw error
        }
        return result
      } catch (error: any) {
        // Handle network and other errors
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          throw new Error('Database connection failed. Please check your internet connection.')
        }
        throw error
      }
    },
    {
      retry: { maxRetries: 1 },
      errorMessage: 'Failed to save data'
    }
  ),

  // Update operations with error handling
  update: createApiOperation(
    async (table: string, data: any, match: any, options: any = {}) => {
      try {
        const { data: result, error } = await supabase
          .from(table)
          .update(data)
          .match(match)
          .select(options.select || '*')

        if (error) {
          // Handle schema evolution issues gracefully
          if (error.code === '42703' || error.message?.includes('column') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Column missing in table '${table}'. Cannot update data.`, error)
            throw new Error(`Database schema issue: Missing columns in table '${table}'. Please run database migrations.`)
          }
          if (error.code === '42P01' || error.message?.includes('relation') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Table '${table}' does not exist. Cannot update data.`, error)
            throw new Error(`Database schema issue: Table '${table}' does not exist. Please run database migrations.`)
          }
          throw error
        }
        return result
      } catch (error: any) {
        // Handle network and other errors
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          throw new Error('Database connection failed. Please check your internet connection.')
        }
        throw error
      }
    },
    {
      retry: { maxRetries: 1 },
      errorMessage: 'Failed to update data'
    }
  ),

  // Delete operations with error handling
  delete: createApiOperation(
    async (table: string, match: any, options: any = {}) => {
      try {
        const { data, error } = await supabase
          .from(table)
          .delete()
          .match(match)
          .select(options.select || '*')

        if (error) {
          // Handle schema evolution issues gracefully
          if (error.code === '42P01' || error.message?.includes('relation') && error.message?.includes('does not exist')) {
            console.warn(`Schema evolution: Table '${table}' does not exist. Nothing to delete.`, error)
            return [] // Return empty array for missing tables
          }
          throw error
        }
        return data
      } catch (error: any) {
        // Handle network and other errors
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          throw new Error('Database connection failed. Please check your internet connection.')
        }
        throw error
      }
    },
    {
      retry: { maxRetries: 1 },
      errorMessage: 'Failed to delete data'
    }
  ),

  // Auth operations with enhanced error handling
  auth: {
    signIn: createApiOperation(
      async (credentials: { email: string; password: string }) => {
        const { data, error } = await supabase.auth.signInWithPassword(credentials)
        if (error) throw error
        return data
      },
      {
        errorMessage: 'Sign in failed. Please check your credentials.'
      }
    ),

    signUp: createApiOperation(
      async (credentials: { email: string; password: string; options?: any }) => {
        const { data, error } = await supabase.auth.signUp(credentials)
        if (error) throw error
        return data
      },
      {
        errorMessage: 'Account creation failed. Please try again.'
      }
    ),

    signOut: createApiOperation(
      async () => {
        const { error } = await supabase.auth.signOut()
        if (error) throw error
      },
      {
        errorMessage: 'Sign out failed.'
      }
    ),

    resetPassword: createApiOperation(
      async (email: string) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email)
        if (error) throw error
      },
      {
        errorMessage: 'Failed to send password reset email.'
      }
    ),
  },

  // Storage operations with error handling
  storage: {
    upload: createApiOperation(
      async (bucket: string, path: string, file: File, options: any = {}) => {
        const { data, error } = await supabase.storage
          .from(bucket)
          .upload(path, file, options)

        if (error) throw error
        return data
      },
      {
        errorMessage: 'File upload failed.'
      }
    ),

    download: createApiOperation(
      async (bucket: string, path: string) => {
        const { data, error } = await supabase.storage
          .from(bucket)
          .download(path)

        if (error) throw error
        return data
      },
      {
        errorMessage: 'File download failed.'
      }
    ),

    delete: createApiOperation(
      async (bucket: string, paths: string[]) => {
        const { data, error } = await supabase.storage
          .from(bucket)
          .remove(paths)

        if (error) throw error
        return data
      },
      {
        errorMessage: 'File deletion failed.'
      }
    ),
  },
}